/**
 * Unified Shade Modal - Single Responsive Swiper
 * Combines desktop and mobile swipers into one responsive instance
 * Performance improvements: 50% fewer Swiper instances, unified event handling
 */

class ShadeModalUnified {
  constructor() {
    this.shadeData = null;
    this.currentSlide = 0;
    this.videoCache = new Map();
    this.initObserver = null;
    this.visibilityObserver = null;
    this.unifiedSwiper = null;
    this.isMobile = window.innerWidth < 992;

    this.init();
  }

  init() {
    // Focus modal for keyboard events (fixes Escape key on Firefox)
    const modalDialog = document.querySelector('#PopupModal-shadefinder');
    if (modalDialog) {
      setTimeout(() => modalDialog.focus(), 100);
    }

    // Setup focus trapping to keep Tab within modal
    this.setupFocusTrap();

    // Reset video playback state but keep src intact for performance
    document.querySelectorAll('#swiper-wrapper-unified video').forEach((video) => {
      try {
        video.pause();
        video.currentTime = 0;
      } catch (e) {}

      // Don't clear src - keep it for performance on reopen
      // Just reset the loaded flag so we can replay
      video.dataset.loaded = '';
    });

    // Clear the cache to force reload check
    this.videoCache.clear();

    // If we already have shade data, reuse it (prevents data loss on modal reopen)
    let shadeData = this.shadeData;

    // Only extract from HTML if we don't have cached data
    if (!shadeData) {
      shadeData = this.extractShadeDataFromHTML();

      if (!shadeData || shadeData.length === 0) {
        console.warn('No shade data found');
        return;
      }
    }

    this.shadeData = shadeData;

    // Render all slides with responsive video sources (this loads initial videos 0-2)
    this.renderUnifiedSlider();

    // Setup lazy loading observer
    this.setupLazyLoading();

    // Setup form submission delegation
    this.setupFormSubmissionDelegation();

    // Initialize Swiper after rendering
    setTimeout(() => {
      console.log('Initializing Swiper...');
      this.initializeSwiper();
      this.markLoadersAsReady();

      // Setup visibility observer AFTER videos have src set
      this.setupVideoVisibilityObserver();

      this.navigateToTargetSlide();
    }, 100);

    // Handle window resize for responsive behavior
    this.setupResponsiveHandling();
  }

  /**
   * Extract shade data from HTML structure - ONLY from video swiper wrapper
   */
  extractShadeDataFromHTML() {
    // CRITICAL: Only look inside the unified swiper wrapper, NOT globally
    const wrapper = document.getElementById('swiper-wrapper-unified');
    if (!wrapper) {
      console.warn('[SWIPER] Wrapper not found, cannot extract data!');
      return null;
    }

    const slides = wrapper.querySelectorAll('[data-mediaID]');

    if (slides.length === 0) {
      console.warn('[SWIPER] No slides found in swiper wrapper!');
      return null;
    }

    const data = Array.from(slides).map((slide) => {
      const mediaID = slide.getAttribute('data-mediaID');
      const video = slide.querySelector('video');
      const sourceEl = video?.querySelector('source');

      return {
        variantId: mediaID,
        videoDesktop: sourceEl?.getAttribute('data-src-desktop') || '',
        videoMobile: sourceEl?.getAttribute('data-src-mobile') || '',
      };
    });

    return data;
  }

  /**
   * Render unified video slider with responsive sources
   */
  renderUnifiedSlider() {
    const wrapper = document.getElementById('swiper-wrapper-unified');

    if (!wrapper) {
      console.warn('[SWIPER] Unified swiper wrapper not found');
      return;
    }

    // The wrapper is already populated by Liquid template with the correct slides
    // Videos already have data-src-desktop and data-src-mobile attributes
    // Load only adjacent slides for performance
    this.loadVideoRange(0, 2);
  }

  /**
   * Initialize Swiper with responsive configuration
   */
  initializeSwiper() {
    const swiperEl = document.querySelector('#shade-finder-slider-unified');

    if (!swiperEl) {
      console.warn('[SWIPER] Swiper container not found');
      return;
    }

    // If Swiper already initialized, destroy it
    if (this.unifiedSwiper) {
      console.log('[SWIPER] Destroying existing swiper instance');
      this.unifiedSwiper.destroy();
    }

    // Responsive breakpoint config
    const config = {
      loop: false,
      slidesPerView: 1,
      spaceBetween: 15,
      grabCursor: true,
      cssMode: this.isMobile,
      allowTouchMove: true,
      controller: {
        control: '.swiper-one'
      },
      navigation: {
        nextEl: '.swiper-button-next-unified',
        prevEl: '.swiper-button-prev-unified',
        disabledClass: 'swiper-button-disabled'
      },
      on: {
        slideChange: (swiper) => {
          this.onSlideChange(swiper);
          this.updateButtonStates(swiper);
        },
        init: (swiper) => {
          this.loadVideoForSlide(swiper.activeIndex);
          this.updateButtonStates(swiper);
        }
      }
    };

    // Create unified Swiper instance
    try {
      this.unifiedSwiper = new Swiper('#shade-finder-slider-unified', config);
    } catch (e) {
      console.error('[SWIPER] Error creating Swiper:', e);
    }
  }

  /**
   * Load video source based on device type
   */
  loadVideoForSlide(index) {
    if (index < 0 || index >= this.shadeData.length) {
      console.warn('[SWIPER] Index out of range:', index);
      return;
    }

    const shade = this.shadeData[index];
    const videoUrl = this.isMobile ? shade.videoMobile : shade.videoDesktop;

    // Skip slides without videos
    if (!videoUrl) {
      return;
    }

    // Find the video element by index
    const videos = document.querySelectorAll('#swiper-wrapper-unified video');
    const video = videos[index];

    if (!video) {
      console.warn('[SWIPER] Video element not found at index', index);
      return;
    }

    const sourceEl = video.querySelector('source');
    if (!sourceEl) {
      console.warn('[SWIPER] Source element not found at index', index);
      return;
    }

    const currentSrc = sourceEl.src;
    if (currentSrc !== videoUrl) {
      sourceEl.src = videoUrl;
      // IMPORTANT: Keep data attributes for reopen - don't remove them!

      // Reset video and load
      video.currentTime = 0;
      video.load();

      // Small delay to let video load, then play
      setTimeout(() => {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('[SWIPER]   ✓ Playing');
            })
            .catch(error => {
              console.log('[SWIPER]   ✗ Play error:', error.message);
            });
        }
      }, 50);

      this.videoCache.set(shade.variantId, true);
      video.dataset.loaded = 'true';
    } else {
      if (video.paused && video.src) {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log('[SWIPER]   Resume error:', error.message);
          });
        }
      }
    }
  }

  /**
   * Load video sources for a range of slides
   */
  loadVideoRange(startIndex, endIndex) {
    const safeEnd = Math.min(endIndex, this.shadeData.length - 1);

    for (let i = startIndex; i <= safeEnd; i++) {
      this.loadVideoForSlide(i);
    }
  }

  /**
   * Handle slide changes - load adjacent slides for smooth navigation
   */
  onSlideChange(swiper) {
    this.currentSlide = swiper.activeIndex;

    // Load current slide + adjacent slides
    const prevIndex = Math.max(0, this.currentSlide - 1);
    const nextIndex = Math.min(this.shadeData.length - 1, this.currentSlide + 1);

    this.loadVideoRange(prevIndex, nextIndex);
  }

  /**
   * Update button aria-disabled state based on swiper position
   */
  updateButtonStates(swiper) {
    const prevBtn = document.querySelector('.swiper-button-prev-unified');
    const nextBtn = document.querySelector('.swiper-button-next-unified');

    const isFirst = swiper.activeIndex === 0;
    const isLast = swiper.activeIndex === swiper.slides.length - 1;

    if (prevBtn) {
      prevBtn.setAttribute('aria-disabled', isFirst ? 'true' : 'false');
      prevBtn.style.opacity = isFirst ? '0.2' : '1';
      prevBtn.style.cursor = isFirst ? 'not-allowed' : 'pointer';
      prevBtn.style.pointerEvents = isFirst ? 'none' : 'auto';
    }
    if (nextBtn) {
      nextBtn.setAttribute('aria-disabled', isLast ? 'true' : 'false');
      nextBtn.style.opacity = isLast ? '0.2' : '1';
      nextBtn.style.cursor = isLast ? 'not-allowed' : 'pointer';
      nextBtn.style.pointerEvents = isLast ? 'none' : 'auto';
    }
  }

  /**
   * Setup Intersection Observer for lazy loading videos
   */
  setupLazyLoading() {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.01
    };

    this.initObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const video = entry.target;
          if (video.tagName === 'VIDEO') {
            const sourceEl = video.querySelector('source[data-src-desktop]');

            if (sourceEl && !video.dataset.loaded) {
              const videoUrl = this.isMobile
                ? sourceEl.dataset.srcMobile
                : sourceEl.dataset.srcDesktop;

              sourceEl.src = videoUrl;
              video.load();
              video.dataset.loaded = 'true';
              this.initObserver.unobserve(video);
            }
          }
        }
      });
    }, options);

    // Observe all video elements
    document
      .querySelectorAll('#swiper-wrapper-unified video')
      .forEach((el) => {
        this.initObserver.observe(el);
      });
  }

  /**
   * Setup observer for pausing videos outside viewport
   */
  setupVideoVisibilityObserver() {
    const wrapper = document.querySelector('.swiper-wrapper-unified');
    if (!wrapper) return;

    const videos = wrapper.querySelectorAll('video');
    if (!videos.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.target.tagName !== 'VIDEO') return;

        if (entry.isIntersecting) {
          // Video is visible - try to play it
          if (video.src || video.querySelector('source')?.src) {
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch((error) => {
                console.log('[SWIPER] Auto-play blocked:', error.message);
              });
            }
          }
        } else {
          // Video is not visible - pause it
          video.pause();
        }
      });
    }, { threshold: 0.1 });

    videos.forEach((video) => observer.observe(video));
    this.visibilityObserver = observer;
  }

  /**
   * Delegate form submission
   */
  setupFormSubmissionDelegation() {
    const container = document.querySelector('.shade-modal');
    if (!container) return;

    container.addEventListener('submit', (e) => {
      if (e.target.matches('[data-product-form]')) {
        // Form submission handled
      }
    });
  }

  /**
   * Handle window resize - update swiper config and video sources
   */
  setupResponsiveHandling() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth < 992;

        // Only reinitialize if breakpoint changed
        if (wasMobile !== this.isMobile) {
          // Reload current slide with appropriate video source
          this.loadVideoForSlide(this.currentSlide);

          // Update Swiper config if needed
          if (this.unifiedSwiper) {
            this.unifiedSwiper.params.cssMode = this.isMobile;
          }
        }
      }, 250);
    });
  }

  /**
   * Mark wrappers as loaded to show content
   */
  markLoadersAsReady() {
    const wrapper = document.getElementById('swiper-wrapper-unified');
    if (wrapper) {
      // Remove and re-add to ensure it's applied
      wrapper.classList.remove('loaded');
      // Force reflow
      void wrapper.offsetWidth;
      wrapper.classList.add('loaded');
    } else {
      console.warn('[SWIPER] swiper-wrapper-unified not found!');
    }
  }

  /**
   * Navigate to target slide by variant ID
   */
  navigateToTargetSlide() {
    if (!this.unifiedSwiper) return;

    const targetSlideId = document.getElementById('main_variant_id')?.value;
    if (!targetSlideId) return;

    const slideIndex = this.shadeData.findIndex(
      (shade) => shade.variantId === targetSlideId
    );

    if (slideIndex !== -1) {
      this.unifiedSwiper.slideTo(slideIndex);
    } else {
      console.warn(`Slide with variant ID "${targetSlideId}" not found`);
    }
  }

  /**
   * Navigate to slide by variant ID (for external calls)
   */
  goToSlideById(variantId) {
    if (!this.unifiedSwiper) return;

    const slideIndex = this.shadeData.findIndex(
      (shade) => shade.variantId === variantId
    );
    if (slideIndex !== -1) {
      this.unifiedSwiper.slideTo(slideIndex);
    }
  }

  /**
   * Cleanup on modal close
   */
  destroy() {
    // Just disconnect observers, let garbage collection handle the rest
    if (this.initObserver) {
      this.initObserver.disconnect();
    }
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }
    if (this.unifiedSwiper) {
      this.unifiedSwiper.destroy();
    }
  }

  /**
   * Setup focus trap to keep Tab navigation within modal
   */
  setupFocusTrap() {
    const modal = document.querySelector('#PopupModal-shadefinder');
    if (!modal) return;

    // Sync swipers when Add to Cart buttons are focused
    const addButtons = modal.querySelectorAll('button[name="add"]');
    addButtons.forEach((btn, index) => {
      btn.addEventListener('focus', () => {
        // Sync both swipers to this shade index
        if (this.unifiedSwiper) {
          this.unifiedSwiper.slideTo(index, 0); // 0 = no animation
        }
      });
    });

    modal.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      // Get all focusable elements within modal
      const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Trap focus
      if (e.shiftKey) {
        // Shift+Tab on first element -> focus last element
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab on last element -> focus first element
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    });
  }
}

// Expose globally for use from template-product.js
window.ShadeModalUnified = ShadeModalUnified;
let shadeModalUnified = null;

// Initialize unified modal when modal opens
// This is triggered by template-product.js when user clicks shade-finder button
function initializeUnifiedShadeModal() {
  if (document.querySelector('#shade-finder-slider-unified')) {
    // ALWAYS create a fresh instance - don't try to reuse
    shadeModalUnified = new ShadeModalUnified();
  } else {
    console.warn('[SWIPER] shade-finder-slider-unified not found!');
  }
}

// Export for template-product.js
window.initializeUnifiedShadeModal = initializeUnifiedShadeModal;

// FALLBACK: Auto-initialize if using unified swiper and modal becomes visible
document.addEventListener('DOMContentLoaded', () => {
  const unifiedContainer = document.querySelector('#shade-finder-slider-unified');
  if (unifiedContainer) {
    // Wait for modal to be shown, then auto-initialize if not already done
    const observer = new MutationObserver(() => {
      const modal = document.querySelector('#PopupModal-shadefinder .modal');
      if (modal && modal.classList.contains('open') && !shadeModalUnified) {
        initializeUnifiedShadeModal();
      }
    });

    observer.observe(document.querySelector('#PopupModal-shadefinder'), {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });
  }
});

// Modal close handler
document.addEventListener('DOMContentLoaded', () => {
  const closeBtn = document.querySelector('[id="ModalClose-PopupModal-shadefinder"]');
  if (closeBtn) {
    closeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const cameraVideo = document.getElementById('camera');
      if (cameraVideo) {
        stopStreamedVideo(cameraVideo);
      }
      if (shadeModalUnified) {
        shadeModalUnified.destroy();
        shadeModalUnified = null;
      }
    });
  }
});

// Import from template-product.js
function stopStreamedVideo(videoElem) {
  const stream = videoElem.srcObject;
  if (stream) {
    const tracks = stream.getTracks();
    tracks.forEach((track) => {
      track.stop();
    });
    videoElem.srcObject = null;
  }
}
